import { getConfig } from '@superbet-group/web.lib.config';
import {
    AuthenticationError,
    differenceInYears,
    addDays,
    isFailure,
    Result,
} from '@superbet-group/web.lib.utilities';
import { ActionContext } from 'vuex';
import { IState } from '@app/store';
import User, { Session } from '@core/models/user/User';
import AuthenticationService from '@core/services/authentication/AuthenticationService';
import { ExploitLogoutError } from '@core/services/authentication/types';
import { SET_USER_DATA } from '@store/modules/data/authentication/mutationTypes';
import modalCodes from '@store/modules/ui/shared/modal/modalCodes';

import { IAuthenticationState } from './authenticationIndex';
import { shellLocalConfig } from '@src/config/shell/localConfig';
import {
    getLocaleRedirectUrl,
    isLocaleMismatchOnMultiLingualTarget,
} from '@app/localization/setLocale';
import {
    AnalyticsLifecycleManager,
    Analytics as AnalyticsSDK,
} from '@superbet-group/web.analytics.sdk';
import { checkLicencePreferences } from '@accountFederation/countries/be/VanHeckeManager';
import {
    clearComingFromLangRedirect,
    isComingFromLangRedirect,
    markComingFromLangRedirect,
} from '@modules/authentication/isComingFromLangRedirect';
import { getAccountConfig } from '@accountFederation/config/accountConfig';
import { SiteLanguageConfig } from '@modules/app/siteLanguage/siteLanguageConfig';
import { getIsUsingGeoLocationCheck } from '@core/services/location/helpers';
import { lastDataValidationShownYear } from '@accountFederation/modules/idCardVerification/utils/annualDataValidation';
import { RouteName } from '@src/app/router/constants/RouteName';
import { TimeFilterSlug } from '@store/modules/ui/shared/timeFilter/utils';
import { featureFlagService } from '@superbet-group/web.lib.flags';
import { AccountFlagNames } from '@modules/userProfile/account/FeatureFlags';
import { processReKycModals } from '@store/modules/data/authentication/kycHelper';
import { getShellConfig } from '@src/config/shell/config';
import { isMultiLoginTarget } from '@app/multiLogin/isMultiLoginTarget';
import {
    clearNewLoginMark,
    isNewLoginMarkSet,
} from '@app/multiLogin/isNewLogin';
import { BooleanExperimentsSegment } from '@superbet-group/product.analytics';

type Context = ActionContext<IAuthenticationState, IState>;
interface DependenciesPostLogin {
    getArePushNotificationEnabled: () => Promise<boolean>;
    saveSessionStart: (context: Context, timestamp?: number) => void;
    setIsReturningUserDevice: (context: Context) => void;
    setSessionState: (user: User, context: Context) => void;
    scheduleGeoLocationPing: (user: User, context: Context) => void;
    pushNotificationsService: {
        connect: (playerId: string, sessionId: string) => void;
        disconnect: () => void;
    };
    setHelperUserData: () => void;
    initializePersonalisedConfig: (user: User) => void;
}

interface DependenciesPostLogout {
    getArePushNotificationEnabled: () => Promise<boolean>;
    clearSessionState: (context: Context) => void;
    pushNotificationsService: {
        connect: (playerId: string, sessionId: string) => void;
        disconnect: () => void;
    };
    clearHelperUserData: () => void;
    behindLoginAction: (context: Context) => void;
    initializeAnonymousUserConfig: () => void;
    clearUserLocationListener: () => void;
}

export function createPostLogin({
    getArePushNotificationEnabled,
    saveSessionStart,
    setIsReturningUserDevice,
    setSessionState,
    scheduleGeoLocationPing,
    pushNotificationsService,
    setHelperUserData,
    initializePersonalisedConfig,
}: DependenciesPostLogin) {
    // eslint-disable-next-line sonarjs/cognitive-complexity
    return async function postLogin(context: Context, { user, isLogin = false }:
        { user: User; isLogin: boolean },
    ): Promise<void> {
        // For multi-login domains use localstorage identifier instead of `isLogin`
        const isNewLogin = (isMultiLoginTarget ? isNewLoginMarkSet() : isLogin) || isComingFromLangRedirect();
        const shellConfig = getShellConfig();

        // check if the user has registered after the initial experiment date for PE-156.
        if (isLogin && (shellConfig.remoteConfig.experiments?.minimalRegistrationDateForRedirectAfterLogin
            ? user.registrationDate.getTime() >= shellConfig.remoteConfig.experiments.minimalRegistrationDateForRedirectAfterLogin.getTime()
            : true
        )) {
            // the three values below are imported like this due to an error in the tests when importing them at the top of the file
            // this error happens only in the tests, and never in the actual application
            // this is most probably due to the circular dependency between the router and another module
            const { default: router } = await import('@src/app/router');
            const { default: store } = await import('@app/store');
            const { marketingLandingRoutes } = await import('@src/app/router/routes/marketingLanding');
            const isAtLivePage = store.getters['navigation/isOnSportLivePage'] as boolean;
            const isLandingRoute = marketingLandingRoutes.some((landingRoute) => landingRoute.name === router.currentRoute.name);

            const limitDateForRedirection = addDays(30, user.registrationDate);

            if (Date.now() < limitDateForRedirection.getTime() && !isLandingRoute && !isAtLivePage) {
                // the user is a potential target for the experiment:
                // - user registered in the last 30 days
                // - user is not on a landing page
                // - user is not on the live page already
                const isLivePageShownAfterLoginExperimentActive = await featureFlagService.getFeatureValue<boolean | undefined>(AccountFlagNames.isLivePageShownAfterLogin, undefined);
                if (isLivePageShownAfterLoginExperimentActive !== undefined) {
                    let group;
                    if (isLivePageShownAfterLoginExperimentActive) {
                        router.push({
                            name: RouteName.Sport.CategoryPerTime,
                            params: { timeFilter: TimeFilterSlug.Live },
                        });

                        // in this experiment, group B (experiment group) means "user was redirected to live page"
                        group = BooleanExperimentsSegment.B;
                    } else {
                        // in this experiment, group A (control group) means "user was not redirected to live page"
                        group = BooleanExperimentsSegment.A;
                    }
                    AnalyticsSDK.getInstance().log('PE_156_Login_Redirection_Variant_Activated', {
                        group,
                    });
                }
            }
        }

        if (isLocaleMismatchOnMultiLingualTarget(user.locale, getConfig().appLocale.locale) && !getConfig().environment.isDevelopment) {
            const availableLocales = SiteLanguageConfig.availableLocales
                .map((selectableLocale) => selectableLocale.value);

            // Redirect only if the user's locale exists in the app
            // .com environment can potentially have users with locales that don't match the app locales
            if (availableLocales!.includes(user.locale!)) {
                markComingFromLangRedirect();
                window.location.href = getLocaleRedirectUrl(user.locale!, context.rootGetters['navigation/route'].path).href;
                return;
            }
        }
        if (isNewLogin) {
            saveSessionStart(context);
        }
        if (shellLocalConfig.isGeolocsScheduledPingEnabled && await getIsUsingGeoLocationCheck()) {
            scheduleGeoLocationPing(user, context);
        }
        setIsReturningUserDevice(context);
        setSessionState(user, context);
        context.dispatch(
            'setIsCheckedAuthentication',
            true,
        );
        context.dispatch('data/captiveKyc/initializeCaptiveKycStore', {}, { root: true });

        if (await getArePushNotificationEnabled()) {
            pushNotificationsService.connect(`${user.userId}`, context.getters.sessionId);
        }
        context.dispatch(
            'ui/inbox/initUserInbox',
            {
                userId: shellLocalConfig.shouldUseUserUuidForInbox ? user.userUuid : user.userId,
                isLogin,
            },
            { root: true },
        );

        context.dispatch('ui/user/messages/showPendingMessages', { isLogin: isNewLogin }, { root: true });
        context.dispatch('ui/promotionHub/bonuses/subscribeToBonusChange', null, { root: true });
        setHelperUserData();
        const config = context.rootGetters['data/country/config'];
        initializePersonalisedConfig(user);

        if (config.countrySpecific.isLastSessionDisplayed && user.lastSession && isNewLogin) {
            context.dispatch(
                'ui/modal/pushModal',
                {
                    code: modalCodes.lastLogin,
                    isClosable: false,
                },
                { root: true },
            );
        }

        if (config.countrySpecific.user?.isLicencePreferencesEnabled && isNewLogin) {
            await checkLicencePreferences(context);
        }

        if (config.countrySpecific.isResponsibleGamblingDisplayedOnLogin) {
            let shouldShowResponsibleGamingModal = !context.getters.hasAcceptedResponsibleGambling && isNewLogin;

            if (context.rootGetters['data/captiveKyc/hasIncompleteVerification']) {
                shouldShowResponsibleGamingModal = false;
            }

            if (shouldShowResponsibleGamingModal) {
                context.dispatch(
                    'ui/modal/addModalToEnd',
                    {
                        code: shellLocalConfig.responsibleGamblingLoginModal,
                        isClosable: false,
                    },
                    { root: true },
                );
            }
        }

        if (getAccountConfig().hasAnnualDataCollection && user.registrationDate) {
            const diffInYears = differenceInYears(new Date(), new Date(user.registrationDate));
            const lastShownYear = parseInt(lastDataValidationShownYear(), 10);
            if (diffInYears >= 1 && lastShownYear < diffInYears) {
                context.dispatch(
                    'ui/modal/pushModal',
                    {
                        code: modalCodes.annualDataValidationModal,
                        isClosable: false,
                    },
                    { root: true },
                );
            }
        }

        if (user.reKycStatus) {
            processReKycModals(context, user.reKycStatus);
        }

        clearNewLoginMark();
        clearComingFromLangRedirect();
    };
}

export function createPostLogout({
    clearSessionState,
    getArePushNotificationEnabled,
    clearHelperUserData,
    pushNotificationsService,
    behindLoginAction,
    initializeAnonymousUserConfig,
    clearUserLocationListener,
}: DependenciesPostLogout) {
    return async function (context: Context): Promise<void> {
        clearSessionState(context);
        if (await getIsUsingGeoLocationCheck()) {
            clearUserLocationListener();
        }
        if (await getArePushNotificationEnabled()) {
            pushNotificationsService.disconnect();
        }
        behindLoginAction(context);
        clearHelperUserData();
        initializeAnonymousUserConfig();
        AnalyticsLifecycleManager.getInstance().clearUserData();
    };
}

export function createIsAuthenticated(
    clearSessionState: (context: Context) => void,
    authenticationService: {
        checkLoginSession: (shouldReturnReasonCode?: boolean) => Promise<Result<Session, null | string>>;
    },
) {
    return async function isAuthenticated(context: Context): Promise<boolean> {
        if (!isMultiLoginTarget && !context.state.user) {
            clearSessionState(context);
            return false;
        }

        context.dispatch('setIsAuthenticating', true);
        const isAutoLogoutEnabled = !!getConfig().countrySpecific.isAutoLogoutEnabled;
        const session = await authenticationService.checkLoginSession(isAutoLogoutEnabled);

        if (isFailure(session)) {
            clearSessionState(context);
            if (isAutoLogoutEnabled) {
                context.dispatch('data/authentication/sessionExpired', session.error);
            }
            context.dispatch('setIsAuthenticating', false);
            return false;
        } else {
            if (isMultiLoginTarget) {
                // For multi-domain set session start time to session `logonTime` for persistency between domains
                context.dispatch('setSessionStartTime', session.value.logonTime);
            }
            if (context.state.user) {
                // Absense of this logic can create subtle bugs as the app is sending sessionId from the memory
                // to ticket cache and cashout. In the context of 2 tabs where one has an old sessionId in memory.
                context.commit(
                    SET_USER_DATA,
                    {
                        key: 'sessionId',
                        value: `${session.value.sessionId}|${session.value.userId}`,
                    });
            }
            context.dispatch('setIsAuthenticating', false);
            return true;
        }
    };
}

export function createLogoutAction(authenticationService: AuthenticationService) {
    return async (
        context: Context,
        doServerLogout = true,
    ) => {
        try {
            if (doServerLogout) {
                await authenticationService.logout();
            }
            context.dispatch('postLogout');
        } catch (e) {
            if (e instanceof AuthenticationError) {
                context.dispatch('postLogout');
                return;
            }
            if (e instanceof ExploitLogoutError) {
                context.dispatch(
                    'ui/modal/setModal',
                    {
                        code: modalCodes.logoutDelay,
                        goBack: true,
                        // to remove the X symbol in to right of modal
                        isClosable: false,
                        data: {
                            subtitle: 'logoutDelay.subtitle',
                            content: 'logoutDelay.message',
                            info: 'logoutDelay.notice',
                        },
                    },
                    { root: true },
                );
                return;
            }
            throw e;
        }
    };
}
